/**
 * Footnote resolution for one chapter (decisions 35, 36, GFM footnotes): which references have a definition,
 * which definition each uses, the number each gets, and what is orphaned or duplicated. Parsing marks the
 * syntax; this decides what it means, because a definition may come after its references.
 *
 * - Labels match as CommonMark link labels do: case-insensitively, on the raw label text (escapes included).
 * - The first definition of a label is the one used; later ones are duplicates.
 * - A reference with no definition is an orphan and reads as its literal text; a definition no reference
 *   uses is an orphan too, kept but unnumbered.
 * - Definitions are numbered from 1 in the order their first reference appears.
 */
import { footnoteLabelEnd } from './footnotes.ts';
import type { ParsedChapter } from './parse.ts';

/** One `[^label]: …` block. */
export interface FootnoteDefinitionEntry {
  /** The label as written. */
  readonly label: string;
  /** The label as matched (case-folded). */
  readonly key: string;
  readonly from: number;
  readonly to: number;
  /** 1-based, in order of first reference; null for a duplicate or an unreferenced definition. */
  readonly number: number | null;
  /** True when an earlier definition has the same key. */
  readonly duplicate: boolean;
}

/** One `[^label]` reference. */
export interface FootnoteReferenceEntry {
  readonly label: string;
  readonly key: string;
  readonly from: number;
  readonly to: number;
  /** Index into `definitions` of the definition it uses, or null for an orphan. */
  readonly definition: number | null;
  /** The number it shows, or null for an orphan. */
  readonly number: number | null;
}

/** Every footnote in a chapter, in document order. */
export interface FootnoteIndex {
  readonly definitions: readonly FootnoteDefinitionEntry[];
  readonly references: readonly FootnoteReferenceEntry[];
}

/** The label as matched: CommonMark's case folding of link labels. */
export function footnoteKey(label: string): string {
  return label.toLowerCase().toUpperCase();
}

/** The label of the footnote node starting at `from` (both kinds open with `[^label]`). */
function labelAt(text: string, from: number): string {
  return text.slice(from + 2, footnoteLabelEnd(text, from));
}

/** Resolves the chapter's footnotes. Pure; linear in the tree. */
export function indexFootnotes({ text, tree }: ParsedChapter): FootnoteIndex {
  const found: Record<'FootnoteDefinition' | 'FootnoteReference', { label: string; from: number; to: number }[]> = {
    FootnoteDefinition: [],
    FootnoteReference: [],
  };
  tree.iterate({
    enter(node) {
      if (node.name === 'FootnoteDefinition' || node.name === 'FootnoteReference') {
        found[node.name].push({ label: labelAt(text, node.from), from: node.from, to: node.to });
      }
    },
  });
  const firstDefinition = new Map<string, number>();
  const seeds = found.FootnoteDefinition.map((f, index) => {
    const key = footnoteKey(f.label);
    const duplicate = firstDefinition.has(key);
    if (!duplicate) firstDefinition.set(key, index);
    return { ...f, key, duplicate };
  });
  const numbers = new Map<number, number>();
  const references = found.FootnoteReference.map((f): FootnoteReferenceEntry => {
    const key = footnoteKey(f.label);
    const definition = firstDefinition.get(key) ?? null;
    let number: number | null = null;
    if (definition !== null) {
      number = numbers.get(definition) ?? numbers.size + 1;
      numbers.set(definition, number);
    }
    return { label: f.label, key, from: f.from, to: f.to, definition, number };
  });
  const definitions = seeds.map(
    (d, index): FootnoteDefinitionEntry => ({ label: d.label, key: d.key, from: d.from, to: d.to, number: numbers.get(index) ?? null, duplicate: d.duplicate }),
  );
  return { definitions, references };
}
